import React, { useState } from 'react';
import { Container, Card, Alert, Navbar, Nav, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import VersionFooter from '../components/VersionFooter';
import ContactUsModal from '../components/ContactUsModal';
import EmailVerificationTokenRequired from '../components/EmailVerificationTokenRequired';
import EmailVerificationCodeRequired from '../components/EmailVerificationCodeRequired';
import LanguageSelector from '../components/LanguageSelector';
import { getSmartRedirectPath } from '../shared/accountCache';

export default function EmailVerificationRequired() {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Redirect if no user or already verified
  React.useEffect(() => {
    const checkAndRedirect = async () => {
      if (!currentUser) {
        navigate('/');
        return;
      }
      
      if (currentUser.emailVerified) {
        // Email is verified - use centralized redirect logic
        const redirectPath = await getSmartRedirectPath(currentUser);
        navigate(redirectPath);
      }
    };
    
    checkAndRedirect();
  }, [currentUser, navigate]);

  if (!currentUser) {
    return null;
  }

  // Determine which component to show based on verificationType
  const isCodeVerification = currentUser.verificationType === 'code';

  return (
    <>
      {/* Top Navbar - same style as HomePage */}
      <Navbar bg="light" expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand href="/" className="fw-bold fs-4 d-flex align-items-center">
            <img src="/logo.svg" alt="trip147" style={{ width: '40px', height: '40px' }} className="me-2" />
            <span className="d-none d-sm-inline">trip147</span>
          </Navbar.Brand>
          <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
            <LanguageSelector variant="outline-secondary" size="sm" />
          </div>
          <Navbar.Toggle aria-controls="email-verification-navbar" />
          <Navbar.Collapse id="email-verification-navbar" className="justify-content-end">
            <Nav className="d-flex flex-column flex-md-row align-items-center gap-2">
              <Button 
                variant="outline-secondary" 
                onClick={handleLogout}
                className="w-100 w-md-auto text-nowrap"
              >
                {t('navigation.logout')}
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
          <Card className="shadow-lg" style={{ maxWidth: '600px', width: '100%' }}>
            <Card.Body className="p-4 text-center">
              {/* Title with icon on same row */}
              <div className="d-flex align-items-center justify-content-center mb-3">
                <i className="bi bi-envelope-exclamation text-warning me-3" style={{ fontSize: '2.5rem' }}></i>
                <h2 className="mb-0">{t('auth.emailVerification.title')}</h2>
              </div>
              
              <Alert variant="info" className="text-start mb-3">
                <p className="mb-2">
                  <strong>{t('auth.emailVerification.welcome', { name: currentUser.name })}</strong>
                </p>
                <p className="mb-0">
                  {t('auth.emailVerification.description')}
                </p>
              </Alert>

              <div className="bg-light p-3 rounded mb-3">
                <p className="mb-2 text-muted small">
                  <i className="bi bi-envelope-fill me-2"></i>
                  {t('auth.emailVerification.emailSentTo')}
                </p>
                <p className="mb-0 fw-bold text-primary">
                  {currentUser.email}
                </p>
              </div>

              {/* Render appropriate component based on verificationType */}
              {isCodeVerification ? (
                <EmailVerificationCodeRequired 
                  currentUser={currentUser}
                  onShowContactModal={() => setShowContactModal(true)}
                />
              ) : (
                <EmailVerificationTokenRequired 
                  currentUser={currentUser}
                  onShowContactModal={() => setShowContactModal(true)}
                />
              )}
            </Card.Body>
          </Card>
        </div>
      </Container>
      <VersionFooter />

      <ContactUsModal 
        show={showContactModal} 
        onHide={() => setShowContactModal(false)}
        defaultMessage={t('auth.emailVerification.contactUsDefaultMessage')}
        source="email_verification_help"
      />
    </>
  );
}
